import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { map, switchMap, timeout } from 'rxjs/operators';

import { Utils } from '../utils.service';

import { ApiService, HttpMethod } from '../api.service';
import { PurchaseOrder } from './purchase-order.model';
import { PurchaseOrdersList } from './purchase-orders-list.model';
import { PurchaseOrderTransaction, PurchaseOrderTransactionPartType } from './purchase-order-transaction';
import { ExportedPurchaseOrderModel } from './exported-purchase-order.model';
import { SasTokenModel } from '../../shared-api/images/sas-token.model';
import { AssignedImageResult } from '../converter/assigned-image-result.model';
import { PurchaseOrderMetals } from './purchase-order-metals.model';
import { DatePipe } from '@angular/common';
import { ExistingPoVendorListItemModel } from './models/exising-po-vendor-list-item.model';
import { PagingRequest } from 'src/app/shared-api/models/paging.model';
import { PagingResponse } from '../vendor/models/vendor-for-new-po.model';
import { AdminEditDataBase } from './models/admin-edit-data-base.model';
import { AdminEditSaveModelBase } from './models/admin-edit-save-base.model';
import { SignaturePageType } from 'src/app/core-ui/purchase-order/purchase-order-pdf/purchase-order-pdf.component';
import { AdminEditDataWestVirginia } from './models/admin-edit-data-west-virginia.model';
import { PurchaseOrderAdditionalData } from './models/additional-data.model';
import { AdminEditSaveModelWestVirginia } from './models/admin-edit-save-west-virginia.model';
import { PurchaseOrderSaveModel } from './models/purchase-order-save.model';

@Injectable({
    providedIn: 'root'
})
export class PurchaseOrderApiService extends ApiService
{
    private _uploadingTransactions = false;
    get uploadingTransactions(): boolean { return this._uploadingTransactions; }

    constructor(protected http: HttpClient, private datePipe: DatePipe)
    {
        super(http);
    }

    //region Read
    getVendorsForExistingPo(locationId: number, includeSubVendors: boolean, options?: PagingRequest): Observable<PagingResponse<ExistingPoVendorListItemModel>> {
        return this.apiRequest<PagingResponse<ExistingPoVendorListItemModel>>(this.getVendorsForExistingPoUrl(locationId, includeSubVendors, options), HttpMethod.Get);
    }
    getImagesAsZip(id: number) {
        return this.http.get(this.getImagesAsZipUrl(id), {responseType: 'blob'});
    }

    getPurchaseOrder(id: number): Observable<PurchaseOrder>
    {
        return this.apiRequest<any>(this.getPurchaseOrderUrl(id), HttpMethod.Get)
            .pipe(map(json => new PurchaseOrder(json)));
    }

    addItemFromVin(vin: string, total: number, convertersCount: number, manufacturer: string, poId: number): Observable<PurchaseOrder>{
        let body = {
            purchaseOrderId: poId,
            vin: vin,
            manufacturer: manufacturer,
            convertersCount: convertersCount,
            totalValue: total
        }
        return this.apiRequest<any>(this.addItemFromVinUrl, HttpMethod.Post, JSON.stringify(body)).pipe(map(json => new PurchaseOrder(json)));
    }

    outputPurchaseOrderAsWordDoc(id: number): Observable<any>
    {
        return this.apiGetRequestForWordDocument(this.outputPurchaseOrderAsWordDocUrl(id));
    }

    getAll(pageIndex: number, pageSize: number, sortField: string, sortOrder: number, selectedFilter?: string): Observable<PurchaseOrdersList>
    {
        const sortOrderString = sortOrder === 1 ? 'asc' : 'desc';
        return this.apiRequest<any>(this.getAllPurchaseOrdersUrl(pageIndex, pageSize, sortField, sortOrderString, selectedFilter), HttpMethod.Get)
            .pipe(map(json => new PurchaseOrdersList(json)));
    }
    getAllWithSearchString(searchString: string, pageIndex: number, pageSize: number, sortField: string, sortOrder: number, selectedFilter?: string): Observable<PurchaseOrdersList>
    {
        const sortOrderString = sortOrder === 1 ? 'asc' : 'desc';
        return this.apiRequest<any>(this.getAllPurchaseOrdersWithSearchStringUrl(searchString, pageIndex, pageSize, sortField, sortOrderString, selectedFilter), HttpMethod.Get)
            .pipe(map(json => new PurchaseOrdersList(json)));
    }

    getAllVendorPOs(pageIndex: number, pageSize: number, sortField: string, sortOrder: number): Observable<PurchaseOrdersList>
    {
        const sortOrderString = sortOrder === 1 ? 'asc' : 'desc';
        return this.apiRequest<any>(this.getAllVendorPurchaseOrdersUrl(pageIndex, pageSize, sortField, sortOrderString), HttpMethod.Get)
            .pipe(map(json => new PurchaseOrdersList(json)));
    }
    getInventoryLists(pageIndex: number, pageSize: number, sortField: string, sortOrder: number, searchString: string = null, selectedFilter?: string): Observable<PurchaseOrdersList>
    {
        const sortOrderString = sortOrder === 1 ? 'asc' : 'desc';
        return this.apiRequest<any>(this.getInventoryListsUrl(pageIndex, pageSize, sortField, sortOrderString, searchString, selectedFilter), HttpMethod.Get)
            .pipe(map(json => new PurchaseOrdersList(json)));
    }

    getInRange(startIndex: number = 0, numItems: number = 0, sortField: string = null, sortOrder: number = 1):
        Observable<PurchaseOrdersList>
    {
        const sortOrderString = sortOrder === 1 ? 'asc' : 'desc';
        return this.apiRequest<any>(this.getPurchaseOrdersInRangeUrl(startIndex, numItems, sortField, sortOrderString), HttpMethod.Get)
            .pipe(map(json => new PurchaseOrdersList(json)));
    }

    searchPurchaseOrdersByDate(startDate: Date, endDate: Date, excludeExported: boolean, searchingCOGS: boolean,
                               excludeUnlocked: boolean, batchId: number):
        Observable<PurchaseOrdersList>
    {
        return this.apiRequest<any>(this.searchPurchaseOrdersByDateUrl(startDate, endDate, excludeExported, searchingCOGS,
                excludeUnlocked, batchId),
            HttpMethod.Get).pipe(map(json => new PurchaseOrdersList(json)));
    }
    
    searchPurchaseOrdersByBatch(excludeExported: boolean, batchId: number):
        Observable<PurchaseOrdersList>
    {
        return this.apiRequest<any>(this.searchPurchaseOrdersByBatchUrl(excludeExported, batchId),
            HttpMethod.Get).pipe(map(json => new PurchaseOrdersList(json)));
    }

    searchCashPurchaseOrdersByDate(startDate: Date, endDate: Date): Observable<PurchaseOrdersList>
    {
        return this.apiRequest<any>(this.searchCashPurchaseOrdersByDateUrl(startDate, endDate),
            HttpMethod.Get).pipe(map(json => new PurchaseOrdersList(json)));
    }
    //endregion

    //region Create/Update/Delete
    createPO(vendorId: number, fromInventoryListId: number = null): Observable<PurchaseOrder>
    {
        const body = { vendorId };
        return this.apiRequest<any>(this.createPoUrl(fromInventoryListId), HttpMethod.Post, JSON.stringify(body))
            .pipe(map((response: any) => new PurchaseOrder(response)));
    }

    deletePO(id: number): Observable<boolean>
    {
        return this.apiRequest<any>(this.deletePoUrl(id), HttpMethod.Delete).pipe(map(() => true));
    }

    changeVendor(purchaseOrderId: number, vendorId: number): Observable<PurchaseOrder>
    {
        const body = { id: purchaseOrderId, vendorId };
        return this.apiRequest<any>(this.changeVendorUrl, HttpMethod.Put, JSON.stringify(body))
            .pipe(map((response: any) => new PurchaseOrder(response)));
    }

    saveBatchNumber(batchId: number, poid: number): Observable<any> {
        let body = {
            poid,
            batchId
        }
        return this.apiRequest<any>(this.setBatchNumberUrl, HttpMethod.Put, JSON.stringify(body));
    }

    setPurchaseDate(id: number, date: Date): Observable<PurchaseOrder>
    {
        const body = { id, purchaseDate: date };
        return this.apiRequest<any>(this.setPurchaseDateUrl, HttpMethod.Put, JSON.stringify(body))
            .pipe(map((response: any) => new PurchaseOrder(response)));
    }

    setPurchaseDateDirectly(id: number, date: string, timezone: string): Observable<PurchaseOrder>
    {
        const body = { id, purchaseDate: date, timeZone: timezone };
        return this.apiRequest<any>(this.setPurchaseDateUrl, HttpMethod.Put, JSON.stringify(body))
            .pipe(map((response: any) => new PurchaseOrder(response)));
    }

    setSignaturePageAndLock(id: number, sellerSignaturePageUrl: string, purchaserSignaturePageUrl: string, saveModel: PurchaseOrderSaveModel, additionalData: PurchaseOrderAdditionalData = null): Observable<PurchaseOrder>
    {
        const body = { 
            id, 
            sellerSignaturePageUrl,
            purchaserSignaturePageUrl, 
            vehicleMake: saveModel.vehicleMake,
            vehicleModel: saveModel.vehicleModel,
            vehicleColor: saveModel.vehicleColor,
            vehicleNumber: saveModel.vehicleNumber,
            vehicleState: saveModel.vehicleState,
            signaturePageType: saveModel.signaturePageType,
            additionalData,
            contactId: saveModel.contactId,
            placeOfSaleLocationAddress: saveModel.placeOfSellAddress,
            placeOfSaleLocationCity: saveModel.placeOfSellCity,
            placeOfSaleLocationState: saveModel.placeOfSellState,
            placeOfSaleLocationZip: saveModel.placeOfSellZip,
            print: saveModel.print,
            californiaIdNumber: saveModel.californiaIdNumber,
            middleInitial: saveModel.middleInitial,
            placeOfSale: saveModel.placeOfSale,
            totalAdjustment: saveModel.totalAdjustment,
            latitude: saveModel.latitude,
            longitude: saveModel.longitude,
            nonFerrousAdjustment: saveModel.nonFerrousAdjustment,
            catsAdjustment: saveModel.catsAdjustment
        };
        return this.apiRequest<any>(this.setSignaturePageUrl, HttpMethod.Put, JSON.stringify(body))
            .pipe(map((response: any) => new PurchaseOrder(response)));
    }

    setSignatures(purchaseOrderId: number, sellerSignature: string, purchaserSignature?: string): Observable<any>
    {
        let body = {
            sellerSignature,
            purchaserSignature
        };
        return this.apiRequest<any>(this.setSignaturesUrl(purchaseOrderId), HttpMethod.Put, JSON.stringify(body));
    }

    clearExtraPages(id: number): Observable<boolean>
    {
        return this.apiRequest<any>(this.clearExtraPagesUrl(id), HttpMethod.Put).pipe(map(() => true));
    }

    setExtraPages(id: number, url: string): Observable<boolean>
    {
        const body = { id, url };
        return this.apiRequest<any>(this.setExtraPagesUrl, HttpMethod.Put, JSON.stringify(body)).pipe(map(() => true));
    }

    updatePaymentType(
        id: number, 
        paymentType: number,
        totalCash: number,
        totalCheck: number,
        totalWire: number,
        checkNumber: string
    ): Observable<boolean>
    {
        const body = { 
            id, 
            paymentType,
            totalCash,
            totalCheck,
            totalWire,
            checkNumber
        };
        return this.apiRequest<any>(this.updatePaymentTypeUrl, HttpMethod.Put, JSON.stringify(body)).pipe(map(() => true));
    }

    updatePrices(id: number): Observable<PurchaseOrder>
    {
        return this.apiRequest<any>(this.updatePricesUrl(id), HttpMethod.Put)
            .pipe(map((response: any) => new PurchaseOrder(response)));
    }

    lock(id: number, contactId: number, signaturePageType: SignaturePageType, additionalData?: PurchaseOrderAdditionalData, lat?: number, long?: number): Observable<PurchaseOrder>
    {
        if(additionalData)
            return this.apiRequest<any>(this.lockUrl(id, signaturePageType, contactId), HttpMethod.Put, JSON.stringify(additionalData)).pipe(map((response: any) => new PurchaseOrder(response)));
        return this.apiRequest<any>(this.lockUrl(id, signaturePageType, contactId, lat, long), HttpMethod.Put, {}).pipe(map((response: any) => new PurchaseOrder(response)));
    }

    unlock(id: number): Observable<void>
    {
        return this.apiRequest<any>(this.unlockUrl(id), HttpMethod.Put);
    }

    getSasTokenUrl(filename: string): Observable<SasTokenModel>
    {
        return this.apiRequest<SasTokenModel>(this.getSasTokenUrlUrl(filename), HttpMethod.Get)
            .pipe(map(response => new SasTokenModel(response)));
    }

    uploadAttachmentDirectly(file: File, purchaseOrderId: number): Observable<AssignedImageResult> {
        return from(Utils.addTimestampToImage(file, this.datePipe).pipe(
            switchMap((modifiedImage: File) => {
                let form = new FormData();
                form.append('attachment', modifiedImage, modifiedImage.name);

                return this.apiUploadRequest<AssignedImageResult>(this.uploadAttachmentDirectlyUrl(purchaseOrderId), form, 600)
            })
        ))        
    }

    emailPdf(id: number, poNumber: string, blob: Blob): Observable<any>
    {
        let form = new FormData();
        form.append('id', id.toString());
        form.append('pdf', blob, poNumber + '.pdf')

        return this.apiUploadRequest(this.emailPdfUrl, form, 600)
    }

    uploadTransactions(transactions: PurchaseOrderTransaction[]): Observable<number[]>
    {
        if (this._uploadingTransactions) throwError('Transactions already being uploaded');

        const body = {
            purchaseOrderId: transactions[0].purchaseOrderId,
            items: transactions.map(item => ({
                action: item.type,
                lineItemType: item.partType,
                itemId: item.itemId,
                id: item.lineItemId,
                pricePerItem: item.pricePerItem,
                quantity: item.quantity,
                categoryHasCustomPrices: item.categoryHasCustomPrices,
                levelPercentage: item.levelPercentage,
                customPartNumber: item.customPartNumber
            })),
        };

        return this.apiRequest<any>(this.uploadLineItemsUrl, HttpMethod.Post, JSON.stringify(body), 10);
    }

    apiGetRequestForWordDocument(url: string): Observable<any>
    {
        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        return this.http.get(url, { headers, responseType: 'arraybuffer' }).pipe(timeout(15000));
    }

    addImage(id: number, url: string): Observable<AssignedImageResult>
    {
        const info = { id, url };
        return this.apiRequest(this.addImageUrl, HttpMethod.Put, JSON.stringify(info));
    }

    removeImage(id: number, url: string): Observable<any>
    {
        const info = { id, url };
        return this.apiRequest(this.removeImageUrl, HttpMethod.Put, JSON.stringify(info));
    }
    //endregion

    //region Export
    exportPurchaseOrders(purchaseOrderIds: number[], generateCOGS: boolean, nonFerrousOnly: boolean): Observable<ExportedPurchaseOrderModel>
    {
        const body = {
            purchaseOrderIds,
            generateCOGS,
            nonFerrousOnly
        };
        return this.apiRequest<any>(this.exportPurchaseOrdersUrl, HttpMethod.Post, JSON.stringify(body), 120);
    }

    exportPurchaseOrdersWithPGMData(purchaseOrderIds: number[]): Observable<ExportedPurchaseOrderModel>
    {
        const body = {
            purchaseOrderIds
        };
        return this.apiRequest<any>(this.exportPurchaseOrdersWithPGMDataUrl, HttpMethod.Post, JSON.stringify(body), 120);
    }

    exportExtraPagesZip(purchaseOrderIds: number[]): Observable<ExportedPurchaseOrderModel>
    {
        return this.apiRequest<any>(this.exportExtraPagesZipUrl(purchaseOrderIds), HttpMethod.Get);
    }
    
    updateLineItem(purchaseOrderId: number, lineItemId: number, quantity: number | null, levelPercentage: number | null, lineItemType: PurchaseOrderTransactionPartType): Observable<PurchaseOrder> {
        let body = {
            purchaseOrderId,
            lineItemId,
            lineItemType,
            quantity,
            levelPercentage,
        }
        return this.apiRequest<PurchaseOrder>(this.uploadLineItemsUrl, HttpMethod.Patch, body).pipe(map(json => new PurchaseOrder(json)));
    }
    getMetals(poId: number): Observable<PurchaseOrderMetals>
    {
        return this.apiRequest<any>(this.getMetalsUrl(poId), HttpMethod.Get).pipe(map(json => new PurchaseOrderMetals(json)));
    }
    
    getMetalsToShow(poId: number): Observable<PurchaseOrderMetals>
    {
        return this.apiRequest<any>(this.getMetalsToShowUrl(poId), HttpMethod.Get).pipe(map(json => new PurchaseOrderMetals(json)));
    }
    
    updateNotes(id: number, notes: string): Observable<any> {
        let body = {
            id: id,
            notes: notes
        };
        return this.apiRequest<any>(this.updateNotesUrl, HttpMethod.Put, body);
    }

    assignHedge(id: number, hedgeId: number | null): Observable<any> {
        const body = { hedgeId };
        return this.apiRequest<any>(this.assignHedgeUrl(id), HttpMethod.Put, body);
    }

    getAdminEditDataBase(poId: number): Observable<AdminEditDataBase> {
        return this.apiRequest<AdminEditDataBase>(this.getAdminEditDataBaseUrl(poId), HttpMethod.Get);
    }
    getAdminEditDataWestVirginia(poId: number): Observable<AdminEditDataWestVirginia> {
        return this.apiRequest<AdminEditDataWestVirginia>(this.getAdminEditDataWestVirginiaUrl(poId), HttpMethod.Get);
    }
    saveAdminEditDataBase(poId: number, data: AdminEditSaveModelBase): Observable<PurchaseOrder> {
        return this.apiRequest<any>(this.saveAdminEditDataBaseUrl(poId), HttpMethod.Put, JSON.stringify(data))
            .pipe(map((response: any) => new PurchaseOrder(response)))
    }
    saveAdminEditDataWestVirginia(poId: number, data: AdminEditSaveModelWestVirginia): Observable<any> {
        return this.apiRequest<any>(this.saveAdminEditDataWestVirginiaUrl(poId), HttpMethod.Put, JSON.stringify(data))
            .pipe(map((response: any) => new PurchaseOrder(response)))
    }
    // exportAllPurchaseOrders(purchaseOrderSearchByDate: PurchaseOrderPerformSearchByDateModel): Observable<ExportedPurchaseOrderModel>
    // {
    //     return this.apiService.apiRequest(this.apiUrlService.purchaseOrdersExportAllUrl(purchaseOrderSearchByDate), HttpMethod.Get)
    //         .pipe(map(json => new ExportedPurchaseOrderModel(json)));
    // }
    //endregion

    // =============================================================================================================================
    // URLs
    // =============================================================================================================================
    /* tslint:disable:member-ordering */
    private get baseUrl(): string { return this.baseApiUrl + 'purchaseOrders'; }
    private createPoUrl(fromInventoryListId: number = null): string { 
        let url = `${this.baseUrl}/create`; 
        if(fromInventoryListId != null)
            url += `?fromInventoryListId=${fromInventoryListId}`;
        return url;
    }
    private deletePoUrl(id): string { return `${this.baseUrl}/${id}/delete`; }
    private getImagesAsZipUrl(id: number): string { return `${this.baseUrl}/${id}/images` }
    private getMetalsUrl(poId: number): string { return `${this.baseUrl}/${poId}/metals`; }
    private getMetalsToShowUrl(poId: number): string { return `${this.baseUrl}/${poId}/metalsToShow`; }
    private get changeVendorUrl(): string { return `${this.baseUrl}/changeVendor`; }
    private get setBatchNumberUrl(): string { return `${this.baseUrl}/batchNumber`; }
    private get setPurchaseDateUrl(): string { return `${this.baseUrl}/setPurchaseDate`; }
    private get setSignaturePageUrl(): string { return `${this.baseUrl}/setSignaturePage`; }
    private setSignaturesUrl(purchaseOrderId: number): string { return `${this.baseUrl}/${purchaseOrderId}/signatures`; }
    private clearExtraPagesUrl(id: number): string { return `${this.baseUrl}/${id}/clearExtraPages`; }
    private get setExtraPagesUrl(): string { return `${this.baseUrl}/setExtraPages`; }
    private get updatePaymentTypeUrl(): string { return `${this.baseUrl}/updatePaymentType`; }
    private updatePricesUrl(id: number): string { return `${this.baseUrl}/${id}/updatePrices`; }
    private lockUrl(id: number, signaturePageType: SignaturePageType, contactId?: number, lat?: number, long?: number): string { 
        let url = `${this.baseUrl}/${id}/lock?signaturePageType=${signaturePageType}`; 
        if(lat != undefined && long != undefined)
            url += `&latitude=${lat}&longitude=${long}`;
        if(contactId)
            url += `&contactId=${contactId}`;
        return url;
    }
    private unlockUrl(id: number): string { return `${this.baseUrl}/${id}/unlock`; }
    private get addImageUrl(): string { return `${this.baseUrl}/addImage`; }
    private get removeImageUrl(): string { return `${this.baseUrl}/removeImage`; }
    private get uploadLineItemsUrl(): string { return `${this.baseUrl}/lineItems`; }
    private get emailPdfUrl(): string { return `${this.baseUrl}/emailPdf/asBlob`; }
    private getPurchaseOrderUrl(id: number): string { return `${this.baseUrl}/${id}`; }
    private get addItemFromVinUrl(): string { return `${this.baseUrl}/lineItems/vin`; }
    private outputPurchaseOrderAsWordDocUrl(id: number): string { return `${this.baseUrl}/createWordDoc/${id}`; }
    private getAllPurchaseOrdersUrl(pageIndex: number, pageSize: number, sortField: string, sortOrder: string, selectedFilter?: string): string
    {
        let url = `${this.baseUrl}/all?pageIndex=${pageIndex}&pageSize=${pageSize}&sortField=${sortField}&sortOrder=${sortOrder}`;
        if(selectedFilter){
            url += `&Filters[0].FieldName=Vendor.HasCompleteData&Filters[0].Value=${selectedFilter == 'complete' ? 'true' : 'false'}&Filters[0].Operator=eq`
        }
        return url;
    }
    private getAllPurchaseOrdersWithSearchStringUrl(searchString: string, pageIndex: number, pageSize: number, sortField: string, sortOrder: string, selectedFilter?: string): string
    {
        let url = `${this.baseUrl}/all?searchString=${searchString}&pageIndex=${pageIndex}&pageSize=${pageSize}&sortField=${sortField}&sortOrder=${sortOrder}`;
        if(selectedFilter){
            url += `&Filters[0].FieldName=Vendor.HasCompleteData&Filters[0].Value=${selectedFilter == 'complete' ? 'true' : 'false'}&Filters[0].Operator=eq`
        }
        return url;
    }
    private getAllVendorPurchaseOrdersUrl(pageIndex: number, pageSize: number, sortField: string, sortOrder: string): string
    {
        return `${this.baseUrl}/allVendor?pageIndex=${pageIndex}&pageSize=${pageSize}&sortField=${sortField}&sortOrder=${sortOrder}`;
    }
    private getInventoryListsUrl(pageIndex: number, pageSize: number, sortField: string, sortOrder: string, searchString: string = null, selectedFilter?: string): string
    {
        let url = `${this.baseUrl}/inventoryLists?pageIndex=${pageIndex}&pageSize=${pageSize}&sortField=${sortField}&sortOrder=${sortOrder}`;
        if(searchString)
            url = url + `&searchString=${searchString}`;
        
        if(selectedFilter){
            url += `&Filters[0].FieldName=Vendor.HasCompleteData&Filters[0].Value=${selectedFilter == 'complete' ? 'true' : 'false'}&Filters[0].Operator=eq`
        }
        return url;
    }
    private getPurchaseOrdersInRangeUrl(startIndex: number, numItems: number, sortField: string, sortOrder: string): string
    {
        return `${this.baseUrl}/range?startIndex=${startIndex}&numItems=${numItems}&sortField=${sortField}&sortOrder=${sortOrder}`;
    }
    private searchPurchaseOrdersByDateUrl(startDate: Date, endDate: Date, excludeExported: boolean, searchingCOGS: boolean,
                                          excludeUnlocked: boolean, batchId: number): string
    {
        let url = `${this.baseUrl}/byDate?startDate=${Utils.getDateApiString(startDate)}&endDate=${Utils.getDateApiString(endDate)}&excludeExported=${excludeExported}&searchingCOGS=${searchingCOGS}`;
        if(excludeUnlocked)
            url = url + '&Filters[0].FieldName=IsLocked&Filters[0].Operator=eq&Filters[0].value=true'
        if(batchId != null)
            url += '&batchId=' + batchId
        return url;
    }
    private searchPurchaseOrdersByBatchUrl(excludeExported: boolean, batchId: number): string
    {
        let url = `${this.baseUrl}/byBatch?excludeExported=${excludeExported}&batchId=${batchId}`;
        return url;
    }
    private searchCashPurchaseOrdersByDateUrl(startDate: Date, endDate: Date): string
    {
        return `${this.baseUrl}/cashByDate?startDate=${Utils.getDateApiString(startDate)}&endDate=${Utils.getDateApiString(endDate)}`;
    }
    private get exportPurchaseOrdersUrl(): string { return `${this.baseUrl}/export`; }
    private get exportPurchaseOrdersWithPGMDataUrl(): string { return `${this.baseUrl}/exportPGM`; }
    private exportExtraPagesZipUrl(purchaseOrderIds: number[]): string
    {
        const idsParam = purchaseOrderIds.reduce((list, id) => `${list}${list.length === 0 ? '' : '&'}id=${id}`, '');
        return `${this.baseUrl}/exportExtraPagesZip?${idsParam}`;
    }
    private getSasTokenUrlUrl(filename: string): string { return `${this.baseUrl}/sas/${filename}`; }
    private get updateNotesUrl() { return `${this.baseUrl}/notes`; }
    private assignHedgeUrl(id: number): string { return `${this.baseUrl}/${id}/hedge`; }
    private uploadAttachmentDirectlyUrl(poId: number): string { return `${this.baseUrl}/${poId}/attachment`; }
    


    private baseApiV2 = this.baseApiUrl + 'v2/purchaseOrders'
    private getVendorsForExistingPoUrl(locationId: number, includeSubVendors: boolean, options?: PagingRequest): string { 
        let url = `${this.baseApiV2}/vendors?locationId=${locationId}&includeSubVendors=${includeSubVendors}`; 
        if(!options)
            return url;

        url = url + '&' + this.buildPagingRequest(options); 
        return url;
    }

    private getAdminEditDataBaseUrl(poId: number): string {
        return `${this.baseApiV2}/${poId}/admin-edit/base`; 
    }
    private getAdminEditDataWestVirginiaUrl(poId: number): string {
        return `${this.baseApiV2}/${poId}/admin-edit/west-virginia`; 
    }

    private saveAdminEditDataBaseUrl(poId: number): string {
        return `${this.baseApiV2}/${poId}/admin-edit/base`; 
    }
    private saveAdminEditDataWestVirginiaUrl(poId: number): string {
        return `${this.baseApiV2}/${poId}/admin-edit/west-virginia`; 
    }
}
